// JobSpec Templates — list view of canonical specs the user can author.
// Role-gated to SPEC_AUTHORING_EDITORS. Click a row → opens the existing
// JobSpecAuthoringPanel (no new editor logic; pure entry point).
//
// Shows: spec label, archetype, current effective version (registry latest
// or higher overlay), and last-edited timestamp + author from the
// authoring change log.

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileCog, Pencil, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRole } from "@/data/RoleContext";
import {
  JOB_SPECS,
  getLatestCanonicalVersion,
  type JobSpec,
} from "@/data/jobSpec";
import {
  canEditJobSpec,
  resolveEffectiveCanonicalSpec,
} from "@/data/jobSpecAuthoring";
import { useJobSpecAuthoring } from "@/data/JobSpecAuthoringContext";
import { JobSpecAuthoringPanel } from "@/components/jobspec/JobSpecAuthoringPanel";

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function JobSpecTemplates() {
  const navigate = useNavigate();
  const { role } = useRole();
  const { records, getChangeLog } = useJobSpecAuthoring();
  const [editing, setEditing] = useState<JobSpec | null>(null);

  const allowed = canEditJobSpec(role);

  // Reduce JOB_SPECS to one row per specId, taking the registry canonical
  // as base and overlaying authoring records to compute effective version.
  const rows = useMemo(() => {
    const seen = new Map<string, JobSpec>();
    for (const s of JOB_SPECS) {
      if (s.status !== "canonical") continue;
      const prior = seen.get(s.specId);
      if (!prior || s.versionNumber > prior.versionNumber) {
        seen.set(s.specId, s);
      }
    }
    return Array.from(seen.values()).map((base) => {
      const effective = resolveEffectiveCanonicalSpec(base, records) ?? base;
      const log = getChangeLog(base.specId);
      const lastPublished = log.find((e) => e.action === "published");
      const lastChange = log[0];
      return {
        base,
        effective,
        registryLatest: getLatestCanonicalVersion(base.specId) ?? base.versionNumber,
        lastChange,
        lastPublished,
      };
    });
  }, [records, getChangeLog]);

  if (!allowed) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="shadow-none border-dashed">
          <CardContent className="p-6 flex items-start gap-3">
            <Lock className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-semibold">Restricted</p>
              <p className="text-xs text-muted-foreground mt-1">
                JobSpec Templates can only be edited by Super Admin, Owner,
                Admin, Operations Manager, or Head of Installation.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 gap-1.5 text-xs"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <FileCog className="h-4 w-4 text-primary" />
                <h1 className="text-lg font-bold tracking-tight">JobSpec Templates</h1>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Edit photo and checklist requirements for future job bindings.
                Existing bound jobs keep their original snapshot.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
            {rows.length} templates
          </Badge>
        </div>

        <Card className="shadow-none">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase tracking-wider">Template</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Archetype</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Effective Version</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Last Edited</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(({ base, effective, registryLatest, lastChange, lastPublished }) => {
                  const hasOverlay = effective.versionNumber > registryLatest;
                  return (
                    <TableRow
                      key={base.specId}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setEditing(base)}
                    >
                      <TableCell>
                        <div className="font-medium text-sm">{base.label}</div>
                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                          {base.specId}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {base.archetype}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm tabular-nums">v{effective.versionNumber}</span>
                          {hasOverlay && (
                            <Badge variant="outline" className="text-[9px] uppercase tracking-wider border-primary/40 text-primary">
                              Published
                            </Badge>
                          )}
                        </div>
                        {hasOverlay && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            registry v{registryLatest}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {lastChange ? (
                          <div>
                            <div className="text-xs">
                              {formatRelative(lastChange.changedAt)}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              by {lastChange.changedBy}
                              {lastPublished && lastPublished !== lastChange && (
                                <> · last publish {formatRelative(lastPublished.changedAt)}</>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(base);
                          }}
                        >
                          <Pencil className="h-3 w-3" /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <p className="text-[11px] text-muted-foreground">
          Phase names, order, BOM, and material kits are locked at the platform
          level. Editing here only changes required photos and checklist items
          for jobs bound in the future.
        </p>
      </div>

      {editing && (
        <JobSpecAuthoringPanel
          baseSpec={editing}
          open={true}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
